import * as React from 'react';
import {
  Box,
  StackLayout,
  StatusBanner,
  UtilityText,
} from '@leagueplatform/genesis-core';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIntl } from '@leagueplatform/locales';
import { useLocales } from '@web-config-app/core-react';
import { EntityDetailError } from '@web-config-app/core';
import { bannerAnimation } from '../../constants';
import { BannerLinkItem } from '../banner-link-item/banner-link-item.component';
import { CopyButton } from '../copy-button/copy-button.component';

type ErrorItemProps = EntityDetailError & {
  defaultLocale: string;
};

type ErrorBannerProps = {
  title: string;
  onDismissClick: (event: React.MouseEvent) => void;
  defaultLocale: string;
} & (
  | { errorMessage: string; secondaryErrorMessage?: string; errorList?: never }
  | {
      errorMessage?: never;
      secondaryErrorMessage?: never;
      errorList: EntityDetailError[];
    }
);

const SecondaryErrorDetail: React.FC<{ detail: string }> = ({ detail }) => {
  const { formatMessage } = useIntl();
  return (
    <StackLayout
      orientation="horizontal"
      verticalAlignment="center"
      spacing="$half"
    >
      <UtilityText as="p" size="sm" emphasis="subtle">
        {detail}
      </UtilityText>
      <CopyButton
        size="small"
        tooltip={formatMessage({
          id: 'ERROR_COPY_TO_CLIPBOARD_TOOLTIP',
        })}
        priority="tertiary"
        label={formatMessage({ id: 'COPY_ERROR_DETAILS' })}
        hideLabel
        contents={detail}
      />
    </StackLayout>
  );
};

const ErrorItem: React.FC<ErrorItemProps> = ({
  detail,
  path,
  locale, // will only be used for errors in the non-default locale
  defaultLocale,
  secondaryDetail,
}) => {
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const { setLocale } = useLocales();

  // Errors carry locale information so that clicking a link in a
  // non-default-locale error can switch the active locale before
  // navigating to the offending field. `BannerLinkItem` handles the
  // `Link` rendering itself; the `onClick` we hand it runs first to do
  // the locale switch, then we manually `navigate` so the routing
  // happens after the locale change has settled.
  const handleClick = (event: React.MouseEvent) => {
    event.preventDefault();

    if (locale) {
      setLocale(locale);
    } else {
      setLocale(defaultLocale);
    }

    if (path) {
      setTimeout(() => {
        navigate(
          {
            pathname: window.location.pathname,
            search: `?path=${path}`,
          },
          {
            state: locationState,
          },
        );
      }, 0);
    }
  };

  return (
    <BannerLinkItem
      detail={detail}
      path={path}
      onClick={handleClick}
      state={locationState}
    >
      {secondaryDetail && <SecondaryErrorDetail detail={secondaryDetail} />}
    </BannerLinkItem>
  );
};

export const ErrorBanner: React.FC<ErrorBannerProps> = ({
  title,
  errorMessage,
  secondaryErrorMessage,
  errorList,
  onDismissClick,
  defaultLocale,
}) => (
  <StatusBanner
    status="error"
    data-testid="config-error-banner"
    title={title}
    onDismissClick={onDismissClick}
    css={{
      animation: bannerAnimation,
      transformOrigin: 'top',
    }}
  >
    {errorMessage && (
      <Box>
        <UtilityText as="p">{errorMessage}</UtilityText>
        {secondaryErrorMessage && (
          <SecondaryErrorDetail detail={secondaryErrorMessage} />
        )}
      </Box>
    )}
    {errorList && errorList.length > 0 ? (
      <StackLayout
        as="ul"
        spacing="$half"
        css={{
          'li::marker': {
            fontSize: '1rem',
          },
          listStyleType: 'revert',
          marginInlineStart: '$one',
          marginBlockStart: '$quarter',
          typography: '$bodyOne',
          color: '$onSurfaceTextPrimary',
        }}
      >
        {errorList.map((error, index) => {
          const key = `${error.path}-${index}`;
          return (
            <li key={key}>
              <ErrorItem
                detail={error.detail}
                path={error.path}
                locale={error.locale}
                secondaryDetail={error.secondaryDetail}
                defaultLocale={defaultLocale}
              />
            </li>
          );
        })}
      </StackLayout>
    ) : null}
  </StatusBanner>
);
